import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormArray,
  AbstractControl,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { ZoneService } from 'src/app/services/modules/instant-rate-manager/zone.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';

@Component({
  selector: 'app-create-rate-modal',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, NgSelectModule],
  templateUrl: './create-rate-modal.component.html',
  styleUrl: './create-rate-modal.component.scss',
})
export class CreateRateModalComponent implements OnInit {
  // --- INJECTIONS ---
  private modalService = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private rateManagerService = inject(RateManagerService);
  private zoneService = inject(ZoneService);

  // --- STATE: FORMS ---
  rateForm!: FormGroup;
  isSubmitting = false;
  submitted = false;
  currentStep: number = 1;
  isSaveButtonEnabled: boolean = false;

  // --- STATE: CATEGORIES ---
  categoryTypeArray: Array<any> = [];
  selectedCategoryID: number | null = null;
  selectedCategory: string = '';

  // --- STATE: FULL TRUCK LOAD ---
  availableRateTypes: Array<{ id: number; name: string; label: string }> = [];
  ftlRowOptions: Array<Array<{ id: number; name: string; label: string }>> = [];

  // --- STATE: LOCATIONS (Separated Search) ---
  originArray: Array<any> = [];
  destinationArray: Array<any> = [];
  isLoadingOrigin = false;
  isLoadingDestination = false;

  originSearchTerm$ = new Subject<string>();
  destinationSearchTerm$ = new Subject<string>();

  locationTypeArray = [
    { value: 'door', label: 'Door' },
    { value: 'depot', label: 'Depot' },
  ];

  // --- STATE: DYNAMIC FIELDS ---
  isLoadingDynamicFields = false;

  // --- LIFECYCLE ---
  ngOnInit() {
    this.rateForm = this.fb.group({
      rate_category_id: ['', Validators.required],
      rate_category_name: ['', Validators.required],
      origin_id: ['', Validators.required],
      origin_type: ['', Validators.required],
      pickup_type: ['', Validators.required],
      destination_id: ['', Validators.required],
      destination_type: ['', Validators.required],
      delivery_type: ['', Validators.required],
      transit_days: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],

      pallet_fields: this.fb.array([]),
      furniture_fields: this.fb.array([]),
      ftl_fields: this.fb.array([]),
    });

    this.setupOriginSearch();
    this.setupDestinationSearch();
    this.getCategory();
  }

  // --- SEARCH LOGIC (Separate Streams) ---
  private setupOriginSearch() {
    this.originSearchTerm$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => (this.isLoadingOrigin = true)),
      switchMap(term => from(this.getLocations(term)))
    ).subscribe(data => {
      this.originArray = data;
      this.isLoadingOrigin = false;
    });
  }

  private setupDestinationSearch() {
    this.destinationSearchTerm$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => (this.isLoadingDestination = true)),
      switchMap(term => from(this.getLocations(term)))
    ).subscribe(data => {
      this.destinationArray = data;
      this.isLoadingDestination = false;
    });
  }

  async getLocations(term: string) {
    try {
      const payload = { name: term, page_size: 50 };
      const response = await this.zoneService.getProviderZones(payload);
      return (response.result || []).map((item: any) => ({
        value: item.id,
        label: item.name,
        type: item.type || 'door'
      }));
    } catch (error) {
      return [];
    }
  }

  // --- DATA FETCHING ---
  async getCategory() {
    try {
      const response = await this.rateManagerService.getProviderRatesCategories();
      this.categoryTypeArray = response.data.map((item: any) => ({
        value: item.id,
        label: item.name ? item.name.charAt(0).toUpperCase() + item.name.slice(1) : item.name,
        description: item.description,
        meta: item.meta,
        bracket_type: item.bracket_type,
      }));
    } catch (error) {
      this.categoryTypeArray = [];
    }
  }

  // --- EVENT HANDLERS ---
  onOriginSelected(item: any) {
    if (item) this.rateForm.patchValue({ origin_type: 'zone' });
  }

  onDestinationSelected(item: any) {
    if (item) this.rateForm.patchValue({ destination_type: 'zone'});
  }

  onChangeCategory = async (category: any) => {
    this.selectedCategoryID = category.value;
    this.selectedCategory = category.label;
    this.rateForm.patchValue({
      rate_category_id: category.value,
      rate_category_name: category.label,
    });
  };

  // --- STATE: METADATA ---
  hasConfiguredRanges = false;
  configuredRanges: any[] = [];

  // --- WIZARD NAVIGATION ---
  goToPrevStep = () => {
    if (this.currentStep > 1) {
      this.resetDynamicFields();
      this.resetPalletFields();
      this.resetFurnitureFields();
      this.resetFtlFields();
      this.resetStepFields();
      this.submitted = false; // Reset validation state
      this.currentStep--;
      this.isSaveButtonEnabled = this.currentStep > 1;
    }
  };

  goToNextStep = async () => {
    this.resetDynamicFields();
    this.resetPalletFields();
    this.resetFurnitureFields();
    this.resetFtlFields();
    this.resetStepFields();
    this.hasConfiguredRanges = false;
    this.configuredRanges = [];

    if (this.currentStep === 1) {
      if (!this.selectedCategoryID) return;

      this.isLoadingDynamicFields = true;
      const initLocs = await this.getLocations('');
      this.originArray = [...initLocs];
      this.destinationArray = [...initLocs];

      if (this.selectedCategory.toLowerCase() === 'vehicle' || this.selectedCategory.toLowerCase() === 'car') {
        await this.initializeFtlFields();
      }
      if (this.selectedCategory.toLowerCase() === 'pallet') {
        this.initializePalletFields();
      }
      if (this.selectedCategory.toLowerCase() === 'furniture') {
        this.initializeFurnitureFields();
      }
      if (this.selectedCategory.toLowerCase() === 'full truck load') {
        await this.initializeFtlFields();
      }
      this.isLoadingDynamicFields = false;
      this.isSaveButtonEnabled = true;
    }

    this.currentStep++;
  };

  initializePalletFields() {
    const category = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID);
    const meta = category?.meta;

    let ranges: any[] = [];

    if (Array.isArray(meta?.bracket_ranges?.ranges) && meta.bracket_ranges.ranges.length > 0) {
      ranges = meta.bracket_ranges.ranges;
    } else if (meta?.settings) {
      const weightSetting = meta.settings.find((s: any) => s.key === 'weight_ranges' || s.attribute_code === 'weight_ranges');
      if (weightSetting) {
        if (Array.isArray(weightSetting.value)) {
          ranges = weightSetting.value;
        } else if (typeof weightSetting.value === 'string') {
          try {
            ranges = JSON.parse(weightSetting.value);
          } catch (e) { console.error('Failed to parse weight ranges', e); }
        }
      }
    }

    if (ranges.length > 0) {
      this.hasConfiguredRanges = true;
      this.configuredRanges = ranges;
      ranges.forEach(range => this.addPalletField(range));
    } else {
      this.hasConfiguredRanges = false;
      this.configuredRanges = [];
      this.addPalletField({ min: 0, max: 1000, unit: '' });
    }
  }

  // --- FORM HELPERS ---
  resetStepFields() {
    this.rateForm.patchValue({
      origin_id: null, origin_type: null, pickup_type: null,
      destination_id: null, destination_type: null, delivery_type: null,
      transit_days: null,
    });
    Object.values(this.rateForm.controls).forEach(control => {
      control.markAsPristine();
      control.markAsUntouched();
    });
  }

  resetDynamicFields() {
    // no-op: vehicle now uses ftl_fields
  }

  resetPalletFields() {
    (this.rateForm.get('pallet_fields') as FormArray).clear();
  }

  get palletFields(): FormGroup[] {
    return (this.rateForm.get('pallet_fields') as FormArray).controls as FormGroup[];
  }

  addPalletField(range?: { min: number, max: number, unit?: string }) {
    const palletArray = this.rateForm.get('pallet_fields') as FormArray;

    // If we have configured ranges and no specific range was passed (manual add button click),
    // we take the NEXT one from the configuration list.
    if (this.hasConfiguredRanges && !range) {
        // Validation: Don't add if we've reached the limit
        if (palletArray.length >= this.configuredRanges.length) return;

        range = this.configuredRanges[palletArray.length];
    }

    const isReadOnly = this.hasConfiguredRanges;

    const group = this.fb.group({
      range_from: [{ value: range?.min ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      range_to: [{ value: range?.max ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      bracket_rate: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      unit: [{ value: range?.unit ?? '', disabled: true }],
    });

    palletArray.push(group);
  }

  removePalletField(index: number) {
    const palletArray = this.rateForm.get('pallet_fields') as FormArray;
    if (palletArray.length > 1) {
      palletArray.removeAt(index);
    }
  }

  // --- FURNITURE FIELDS ---
  initializeFurnitureFields() {
    const category = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID);
    const meta = category?.meta;

    let ranges: any[] = [];

    if (Array.isArray(meta?.bracket_ranges?.ranges) && meta.bracket_ranges.ranges.length > 0) {
      ranges = meta.bracket_ranges.ranges;
    } else if (meta?.settings) {
      const weightSetting = meta.settings.find((s: any) => s.key === 'weight_ranges' || s.attribute_code === 'weight_ranges');
      if (weightSetting) {
        if (Array.isArray(weightSetting.value)) {
          ranges = weightSetting.value;
        } else if (typeof weightSetting.value === 'string') {
          try {
            ranges = JSON.parse(weightSetting.value);
          } catch (e) { console.error('Failed to parse weight ranges', e); }
        }
      }
    }

    if (ranges.length > 0) {
      this.hasConfiguredRanges = true;
      this.configuredRanges = ranges;
      ranges.forEach(range => this.addFurnitureField(range));
    } else {
      this.hasConfiguredRanges = false;
      this.configuredRanges = [];
      this.addFurnitureField({ min: 0, max: 1000, unit: '' });
    }
  }

  get furnitureFields(): FormGroup[] {
    return (this.rateForm.get('furniture_fields') as FormArray).controls as FormGroup[];
  }

  addFurnitureField(range?: { min: number, max: number, unit?: string }) {
    const furnitureArray = this.rateForm.get('furniture_fields') as FormArray;

    if (this.hasConfiguredRanges && !range) {
      if (furnitureArray.length >= this.configuredRanges.length) return;
      range = this.configuredRanges[furnitureArray.length];
    }

    const isReadOnly = this.hasConfiguredRanges;

    const group = this.fb.group({
      range_from: [{ value: range?.min ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      range_to: [{ value: range?.max ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      bracket_rate: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      unit: [{ value: range?.unit ?? '', disabled: true }],
    });

    furnitureArray.push(group);
  }

  removeFurnitureField(index: number) {
    const furnitureArray = this.rateForm.get('furniture_fields') as FormArray;
    if (furnitureArray.length > 1) {
      furnitureArray.removeAt(index);
    }
  }

  resetFurnitureFields() {
    (this.rateForm.get('furniture_fields') as FormArray).clear();
  }

  // --- FULL TRUCK LOAD FIELDS ---
  async initializeFtlFields() {
    const response: any = await this.rateManagerService.getProviderRatesSubCategories(this.selectedCategoryID!);
    this.availableRateTypes = (response.data || []).map((item: any) => ({
      id: item.id,
      name: item.name,
      label: this.convertToHuman(item.name),
    }));
    this.addFtlField();
  }

  get ftlFields(): FormGroup[] {
    return (this.rateForm.get('ftl_fields') as FormArray).controls as FormGroup[];
  }

  refreshFtlRowOptions() {
    this.ftlRowOptions = this.ftlFields.map((field, index) => {
      const currentId = field.get('rate_type_id')?.value;
      const usedIds = this.ftlFields
        .map((f, i) => i !== index ? f.get('rate_type_id')?.value : null)
        .filter(Boolean);
      return this.availableRateTypes.filter(rt => !usedIds.includes(rt.id) || rt.id === currentId);
    });
  }

  get unusedRateTypes(): Array<{ id: number; name: string; label: string }> {
    const usedIds = this.ftlFields.map(f => f.get('rate_type_id')?.value).filter(Boolean);
    return this.availableRateTypes.filter(rt => !usedIds.includes(rt.id));
  }

  onFtlRateTypeChange() {
    this.refreshFtlRowOptions();
  }

  addFtlField() {
    const ftlArray = this.rateForm.get('ftl_fields') as FormArray;
    if (ftlArray.length >= this.availableRateTypes.length) return;

    const group = this.fb.group({
      rate_type_id: [null, Validators.required],
      rate: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
    });
    ftlArray.push(group);
    this.refreshFtlRowOptions();
  }

  removeFtlField(index: number) {
    const ftlArray = this.rateForm.get('ftl_fields') as FormArray;
    if (ftlArray.length > 1) {
      ftlArray.removeAt(index);
      this.refreshFtlRowOptions();
    }
  }

  resetFtlFields() {
    (this.rateForm.get('ftl_fields') as FormArray).clear();
    this.availableRateTypes = [];
    this.ftlRowOptions = [];
  }

  // --- SUBMISSION ---
  handleSubmitRate = () => {
    this.submitted = true;
    if (this.rateForm.invalid) {
      this.rateForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    const category = this.selectedCategory.toLowerCase();
    if (category === 'pallet') this.handleCreatePalletRate();
    if (category === 'furniture') this.handleCreateFurnitureRate();
    if (category === 'vehicle' || category === 'car') this.handleCreateCarRate();
    if (category === 'full truck load') this.handleCreateFtlRate();
  };

  handleCreateCarRate() {
    const formData = this.rateForm.getRawValue();
    const rates = formData.ftl_fields.map((field: any) => ({
      rate: field.rate?.toString() || '0',
      rate_type_id: +field.rate_type_id,
    }));
    this.rateManagerService.createRate({ ...formData, rates, status: 'in_review' }).subscribe({
      next: () => this.handleSuccess(),
      error: () => (this.isSubmitting = false)
    });
  }

  handleCreatePalletRate() {
    const formData = this.rateForm.getRawValue();
    const bracket_type = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID)?.bracket_type;
    const rateBrackets = formData.pallet_fields.map((field: any, index: number) => ({
      bracket_order: index + 1,
      range_from: +field.range_from,
      range_to: +field.range_to,
      bracket_rate: parseFloat(field.bracket_rate),
      bracket_type,
    }));
    this.rateManagerService.createRate({ ...formData, rate_brackets: rateBrackets, status: 'in_review' }).subscribe({
      next: () => this.handleSuccess(),
      error: (err) => {
        AlertUtil.error(err?.error?.message || 'Failed to create rate.');
        this.isSubmitting = false;
      }
    });
  }

  handleCreateFurnitureRate() {
    const formData = this.rateForm.getRawValue();
    const bracket_type = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID)?.bracket_type;
    const rateBrackets = formData.furniture_fields.map((field: any, index: number) => ({
      bracket_order: index + 1,
      range_from: +field.range_from,
      range_to: +field.range_to,
      bracket_rate: parseFloat(field.bracket_rate),
      bracket_type,
    }));
    this.rateManagerService.createRate({ ...formData, rate_brackets: rateBrackets, status: 'in_review' }).subscribe({
      next: () => this.handleSuccess(),
      error: (err) => {
        AlertUtil.error(err?.error?.message || 'Failed to create rate.');
        this.isSubmitting = false;
      }
    });
  }

  handleCreateFtlRate() {
    const formData = this.rateForm.getRawValue();
    const rates = formData.ftl_fields.map((field: any) => ({
      rate: field.rate?.toString() || '0',
      rate_type_id: +field.rate_type_id,
    }));
    this.rateManagerService.createRate({ ...formData, rates, status: 'in_review' }).subscribe({
      next: () => this.handleSuccess(),
      error: (err) => {
        AlertUtil.error(err?.error?.message || 'Failed to create rate.');
        this.isSubmitting = false;
      }
    });
  }

  private handleSuccess() {
    AlertUtil.success('Rate created successfully.');
    this.closeModal('success');
    this.isSubmitting = false;
  }

  // --- UTIL ---
  convertToHuman = (text: string) => text.trim().replace(/\s+/g, ' ').split(/[_\s]/).filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  closeModal = (result?: string) => {
    this.submitted = false; // Reset validation state
    this.modalService.close(result);
  };

  cancelModal() {
    this.submitted = false; // Reset validation state
    this.modalService.dismiss();
  }

  allowNumbersWithDecimal(event: KeyboardEvent, field: AbstractControl, allowDecimal: boolean) {
    const input = field.value?.toString() || '';
    const char = event.key;
    if (!/[0-9.]/.test(char) || (char === '.' && (!allowDecimal || input.includes('.')))) {
      event.preventDefault();
    }
  }
}
